// client/src/pages/ManufacturerMap.tsx
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Plus, Merge } from 'lucide-react';
import {
  Facility,
  CreateFacilityRequest,
} from '../services/api/domains/facilities/types';
import {
  useFacilitiesPage,
  useFacilitiesByManufacturer,
  useMergeFacilitiesSuggestions,
  useMergeFacilitiesPreview,
  useMergeFacilities,
} from '../services/api/domains/facilities/hooks';
import FacilityModal, { ModalMode } from '../components/modals/FacilityModal';
import GenericMergeModal from '../components/modals/GenericMergeModal';
import { FacilityMergeConflictResolver } from '../components/modals/FacilityMergeConflictResolver';
import useAuth from '../auth/auth';
import TableSearch from 'components/TableSearch';
import {
  SearchMode,
  AdvancedSearchQuery,
  createEmptyAdvancedQuery,
} from 'components/TableSearch/types';
import { facilitiesFieldConfig } from '../config/searchConfigs';
import LgmDataGrid from 'components/LgmDataGrid';
import {
  GridPaginationModel,
  GridSortModel,
  GridFilterModel,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid';
import {
  gridFilterToAdvancedQuery,
  mergeAdvancedQueries,
} from '../utils/dataGridHelpers';
import {
  toDataGridColumns,
  getDefaultColumnVisibility,
} from '../domains/facilities/adapters';
import { useFacilityQTAStatusCounts } from 'services/api/domains/metrics/hooks';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
delete (L.Icon.Default as any).prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const PAGE_SIZE = 50;

const ManufacturerMap: React.FC = () => {
  const { userId, isViewer, isAdmin } = useAuth();

  // Search state
  const [searchMode, setSearchMode] = useState<SearchMode>('basic');
  const [basicSearchTerm, setBasicSearchTerm] = useState('');
  const [advancedSearchQuery, setAdvancedSearchQuery] =
    useState<AdvancedSearchQuery>(createEmptyAdvancedQuery());

  // DataGrid state
  const [gridFilterModel, setGridFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [gridSortModel, setGridSortModel] = useState<GridSortModel>([]);
  const [gridPaginationModel, setGridPaginationModel] =
    useState<GridPaginationModel>({
      page: 0,
      pageSize: PAGE_SIZE,
    });
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(getDefaultColumnVisibility());

  // Modal state
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(
    null
  );
  const [openFacilityModal, setOpenFacilityModal] = useState<ModalMode | null>(
    null
  );

  // Merge modal state
  const [openMergeModal, setOpenMergeModal] = useState(false);
  const [mergeSourceFacility, setMergeSourceFacility] =
    useState<Facility | null>(null);

  // Map modal state
  const [openMapModal, setOpenMapModal] = useState(false);

  const facilitiesParams = useMemo(() => {
    let finalAdvancedQuery = createEmptyAdvancedQuery();

    // Check if grid filters are present
    const hasGridFilters = gridFilterModel.items.length > 0;

    // Start with TableSearch query (basic or advanced)
    if (searchMode === 'advanced') {
      finalAdvancedQuery = advancedSearchQuery;
    }

    // Convert grid filters to advanced query
    if (hasGridFilters) {
      const gridQuery = gridFilterToAdvancedQuery(
        gridFilterModel,
        facilitiesFieldConfig
      );

      if (gridQuery.filterGroups.length > 0) {
        finalAdvancedQuery = mergeAdvancedQueries(
          finalAdvancedQuery,
          gridQuery
        );
      }
    }

    // Check if there are actual filter rules, not just empty groups
    const hasActualFilters = finalAdvancedQuery.filterGroups.some(
      group => group.rules && group.rules.length > 0
    );

    // Use advanced search if we have grid filters OR advanced search mode
    // Only use basic search when in basic mode AND no grid filters
    const params = {
      search:
        searchMode === 'basic' && !hasGridFilters
          ? basicSearchTerm.trim() || undefined
          : undefined,
      advancedSearch: hasActualFilters ? finalAdvancedQuery : undefined,
      sortBy:
        gridSortModel.length > 0
          ? (gridSortModel[0].field as keyof Facility)
          : undefined,
      sortDirection:
        gridSortModel.length > 0
          ? (gridSortModel[0].sort?.toUpperCase() as 'ASC' | 'DESC')
          : undefined,
      limit: gridPaginationModel.pageSize,
      offset: gridPaginationModel.page * gridPaginationModel.pageSize,
    };

    return params;
  }, [
    searchMode,
    basicSearchTerm,
    advancedSearchQuery,
    gridFilterModel,
    gridSortModel,
    gridPaginationModel,
  ]);

  const {
    data: facilities,
    total,
    refetch: refetchFacilities,
    create: createFacility,
    update: updateFacility,
    delete: deleteFacility,
  } = useFacilitiesPage(facilitiesParams);

  // Fetch all facilities for the source facility's manufacturer when merge modal is open
  const manufacturerFacilitiesQuery = useFacilitiesByManufacturer(
    mergeSourceFacility?.manufacturer_id || null
  );
  const manufacturerFacilities = useMemo(
    () =>
      manufacturerFacilitiesQuery.status === 'success'
        ? manufacturerFacilitiesQuery.data.facilities
        : [],
    [manufacturerFacilitiesQuery]
  );

  const facilityQTAStatusCounts = useFacilityQTAStatusCounts();

  // Memoized utility functions
  const formatDate = useCallback((date?: Date | string | null) => {
    if (!date || date === null) return '';

    // Handle both Date objects and ISO strings
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return String(date);

    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }, []);

  const getVendorFacilityStatusIconColor = useCallback((f: Facility) => {
    switch (f.facility_status) {
      case 'APPROVED':
        return 'green';
      case 'INACTIVE':
        return 'yellow';
      case 'DISQUALIFY':
        return 'red';
      case 'R&D ONLY':
        return 'yellow';
      case 'ON HOLD':
        return 'yellow';
      case 'NOT MANUF.':
        return 'yellow';
      default:
        return 'gray';
    }
  }, []);

  // Get CSS class name for row styling based on facility status
  const getFacilityStatusClassName = useCallback((f: Facility) => {
    switch (f.facility_status) {
      case 'APPROVED':
        return 'facility-status-approved';
      case 'INACTIVE':
      case 'R&D ONLY':
      case 'ON HOLD':
      case 'NOT MANUF.':
        return 'facility-status-warning';
      case 'DISQUALIFY':
        return 'facility-status-disqualified';
      default:
        return '';
    }
  }, []);

  const makeIcon = useCallback(
    (color: string) =>
      new L.Icon({
        iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
        shadowUrl:
          'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41],
      }),
    []
  );

  // DataGrid columns from field registry adapter
  const facilitiesDataGridColumns = useMemo(() => toDataGridColumns(), []);

  // Reset DataGrid page when search changes
  // Only reset when there's ACTUAL search content
  useEffect(() => {
    const hasBasicSearch =
      searchMode === 'basic' && basicSearchTerm.trim().length > 0;
    const hasAdvancedFilters = advancedSearchQuery.filterGroups.some(
      group => group.rules && group.rules.length > 0
    );
    const hasAdvancedSearch = searchMode === 'advanced' && hasAdvancedFilters;

    if (hasBasicSearch || hasAdvancedSearch) {
      setGridPaginationModel(prev => ({ ...prev, page: 0 }));
    }
  }, [basicSearchTerm, searchMode, advancedSearchQuery]);

  // Preserve total during loading to prevent DataGrid from resetting pagination
  // When loading starts, total becomes 0, causing DataGrid to think page is invalid
  // Use state instead of ref so updates trigger re-renders
  const [stableTotal, setStableTotal] = useState(total || 0);
  useEffect(() => {
    if (total && total > 0) {
      setStableTotal(total);
    }
  }, [total]);

  // Memoized handlers for modal actions
  const handleAddFacility = useCallback(() => {
    // Create a new facility with all required fields initialized
    const newFacility: Partial<Facility> = {
      facility_name: '',
      manufacturer_id: -1,
      manufacturer_name: '',
      fei_number: '',
      duns_number: '',
      address_line1: '',
      country_code: '',
      city: '',
      state_province: '',
      postal_code: '',
      created_date: new Date(),
      modified_date: new Date(),
      user_id: userId,
    };
    setSelectedFacility(newFacility as Facility);
    setOpenFacilityModal('EDIT');
  }, [userId]);

  const handleDelete = useCallback(
    async (id?: number) => {
      if (!id || !window.confirm('Delete this facility?')) return;
      try {
        await deleteFacility.mutate(id);
        refetchFacilities();
      } catch (err) {
        console.error('❌ Delete facility failed:', err);
      }
    },
    [deleteFacility, refetchFacilities]
  );

  // New unified modal handlers
  const handleSaveFacility = useCallback(
    async (facilityData: Facility) => {
      try {
        console.log('💾 Saving facility data:', facilityData);

        if (selectedFacility?.facility_id) {
          // Edit existing facility - include user_id
          await updateFacility.mutate({
            ...facilityData,
            user_id: userId,
          });
        } else {
          // Add new facility - validate required fields first
          if (!facilityData.manufacturer_id) {
            console.error(
              '❌ A manufacturer is required for facility creation'
            );
            alert('Please select a manufacturer before saving the facility');
            return;
          }

          if (
            !facilityData.facility_name ||
            facilityData.facility_name.trim() === ''
          ) {
            console.error('❌ facility_name is required for facility creation');
            alert('Please enter a facility name before saving');
            return;
          }

          // Extract required fields for create and include user_id
          const createData: CreateFacilityRequest = {
            manufacturer_id: facilityData.manufacturer_id,
            facility_name: facilityData.facility_name.trim(),
            fei_number: facilityData.fei_number,
            duns_number: facilityData.duns_number,
            address_line1: facilityData.address_line1,
            country_code: facilityData.country_code,
            user_id: userId,
          };

          console.log('🚀 Creating facility with data:', createData);
          await createFacility.mutate(createData);
        }
        setOpenFacilityModal(null);
        setSelectedFacility(null);
        refetchFacilities();
      } catch (err) {
        console.error('❌ Save facility failed:', err);
      }
    },
    [
      selectedFacility,
      updateFacility,
      createFacility,
      refetchFacilities,
      userId,
    ]
  );

  const handleCloseFacilityModal = useCallback(() => {
    setOpenFacilityModal(null);
    setSelectedFacility(null);
  }, []);

  // KPI counts - memoized for performance
  const kpiCounts = useMemo(() => {
    const activeCount =
      facilityQTAStatusCounts.status === 'success'
        ? (facilityQTAStatusCounts.data?.activeCount ?? 0)
        : 0;
    const expiringSoonCount =
      facilityQTAStatusCounts.status === 'success'
        ? (facilityQTAStatusCounts.data?.expiringSoonCount ?? 0)
        : 0;
    const expiredCount =
      facilityQTAStatusCounts.status === 'success'
        ? (facilityQTAStatusCounts.data?.expiredCount ?? 0)
        : 0;

    return { activeCount, expiringSoonCount, expiredCount };
  }, [facilityQTAStatusCounts]);

  const { activeCount, expiringSoonCount, expiredCount } = kpiCounts;

  return (
    <div style={styles.containerStyle}>
      {/* Filter/Search/KPI/Buttons */}
      <div style={styles.filterSectionStyle}>
        <div style={styles.filterControlsStyle}>
          <TableSearch
            mode={searchMode}
            onModeChange={setSearchMode}
            basicSearchTerm={basicSearchTerm}
            onBasicSearchChange={setBasicSearchTerm}
            onBasicSearchClear={() => setBasicSearchTerm('')}
            basicPlaceholder="Search facilities..."
            advancedFieldConfigs={facilitiesFieldConfig}
            advancedQuery={advancedSearchQuery}
            onAdvancedSearch={query => {
              setAdvancedSearchQuery(query);
            }}
            onAdvancedClear={() => {
              setAdvancedSearchQuery(createEmptyAdvancedQuery());
            }}
          />
        </div>
        <div style={styles.kpiDashboardInlineStyle}>
          <div style={styles.kpiHeaderStyle}>QTA Expiration Status</div>
          <div style={styles.kpiHorizontalStyle}>
            <div style={styles.kpiStyle}>
              <div style={styles.kpiCountStyle}>{activeCount}</div>
              <div style={styles.kpiLabelSmallStyle}>Active</div>
            </div>
            <div style={styles.kpiStyle}>
              <div style={styles.kpiCountStyle}>{expiringSoonCount}</div>
              <div style={styles.kpiLabelSmallStyle}>Expiring (90d)</div>
            </div>
            <div style={styles.kpiStyle}>
              <div style={styles.kpiCountStyle}>{expiredCount}</div>
              <div style={styles.kpiLabelSmallStyle}>Expired</div>
            </div>
          </div>
        </div>
        <div style={styles.buttonGroupStyle}>
          <button
            onClick={() => setOpenMapModal(true)}
            style={styles.mapButtonStyle}>
            Facility Map
          </button>
          <button onClick={handleAddFacility} style={styles.addButtonStyle}>
            <Plus size={16} style={styles.addButtonIconStyle} /> Add Facility
          </button>
        </div>
      </div>

      <div style={styles.tableContainerStyle}>
        <LgmDataGrid<Facility>
          data={facilities}
          columns={facilitiesDataGridColumns}
          rowCount={stableTotal}
          paginationModel={gridPaginationModel}
          onPaginationModelChange={setGridPaginationModel}
          sortModel={gridSortModel}
          onSortModelChange={setGridSortModel}
          filterModel={gridFilterModel}
          onFilterModelChange={setGridFilterModel}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          getRowId={row => row.facility_id}
          getRowClassName={params => getFacilityStatusClassName(params.row)}
          onInfo={(f: Facility) => {
            setSelectedFacility(f);
            setOpenFacilityModal('VIEW');
          }}
          onEdit={
            !isViewer
              ? (f: Facility) => {
                  setSelectedFacility(f);
                  setOpenFacilityModal('EDIT');
                }
              : undefined
          }
          onDelete={
            !isViewer
              ? (f: Facility) => {
                  handleDelete(f.facility_id);
                }
              : undefined
          }
          actions={
            isAdmin
              ? (f: Facility) => {
                  // Only show merge button if manufacturer has multiple facilities
                  if (f.manufacturer_facility_count <= 1) {
                    return [];
                  }
                  return [
                    {
                      icon: <Merge size={16} />,
                      onAction: () => {
                        setMergeSourceFacility(f);
                        setOpenMergeModal(true);
                      },
                      title: 'Merge Facility',
                      style: { color: '#7c3aed' },
                      hoverStyle: { color: '#5b21b6' },
                    },
                  ];
                }
              : undefined
          }
        />
      </div>

      {/* FacilityModal */}
      {openFacilityModal && selectedFacility && (
        <FacilityModal
          facility={selectedFacility}
          initialMode={openFacilityModal}
          save={handleSaveFacility}
          close={handleCloseFacilityModal}
        />
      )}

      {/* GenericMergeModal for Facilities */}
      {openMergeModal && mergeSourceFacility && (
        <GenericMergeModal<Facility>
          source={mergeSourceFacility}
          allEntities={manufacturerFacilities}
          config={{
            domainDisplayName: 'Facility',
            domainDisplayNamePlural: 'Facilities',
            entityIdField: 'facility_id',
            entityNameField: 'facility_name',
            entityCreatedDateField: 'created_date',
            useMergeSuggestions: useMergeFacilitiesSuggestions,
            useMergePreview: useMergeFacilitiesPreview,
            useMerge: useMergeFacilities,
            renderConflicts: (previewData, resolvedFields, onFieldResolve) => (
              <FacilityMergeConflictResolver
                previewData={previewData}
                resolvedFields={resolvedFields}
                onFieldResolve={onFieldResolve}
              />
            ),
          }}
          onClose={() => {
            setOpenMergeModal(false);
            setMergeSourceFacility(null);
          }}
          onSuccess={() => {
            setOpenMergeModal(false);
            setMergeSourceFacility(null);
            refetchFacilities();
          }}
        />
      )}

      {/* Map Modal */}
      {openMapModal && (
        <div
          style={styles.modalOverlayStyle}
          onClick={() => setOpenMapModal(false)}>
          <div
            style={styles.mapModalContentStyle}
            onClick={e => e.stopPropagation()}>
            <div style={styles.mapModalHeaderStyle}>
              <h2 style={styles.modalTitleStyle}>Facility Map</h2>
              <button
                onClick={() => setOpenMapModal(false)}
                style={styles.modalCloseButtonStyle}>
                ✕
              </button>
            </div>
            <div style={styles.mapModalBodyStyle}>
              <MapContainer
                center={[20, 0]}
                zoom={2}
                style={styles.mapContainer}>
                <TileLayer
                  attribution="&copy; OpenStreetMap contributors"
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {facilities
                  .filter(f => f.latitude && f.longitude)
                  .map(f => (
                    <Marker
                      key={f.facility_id}
                      position={[f.latitude as number, f.longitude as number]}
                      icon={makeIcon(getVendorFacilityStatusIconColor(f))}>
                      <Popup>
                        <b>{f.facility_name}</b>
                        <br />
                        {f.city}, {f.country_code}
                        <br />
                        QTA Status: {f.qta_status ?? 'N/A'}
                        <br />
                        Vendor Status: {f.facility_status ?? 'N/A'}
                        <br />
                        Effective: {formatDate(f.qta_effective_date)}
                        <br />
                        Exp: {formatDate(f.qta_expiration_date)}
                      </Popup>
                    </Marker>
                  ))}
              </MapContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  // Inline KPI Dashboard styles
  kpiDashboardInlineStyle: {
    display: 'flex',
    flexDirection: 'row',
    gap: '1rem',
    padding: '0.75rem 1rem',
    backgroundColor: '#f9fafb',
    borderRadius: '0.5rem',
    border: '1px solid #e5e7eb',
    alignItems: 'center',
  },
  kpiHorizontalStyle: {
    display: 'flex',
    flexDirection: 'row',
    gap: '2rem',
    justifyContent: 'center',
  },
  kpiLabelSmallStyle: {
    fontSize: '0.75rem',
    color: '#6b7280',
  },
  // Main container styles
  containerStyle: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: '1rem',
    gap: '1.5rem',
  },

  // Table container with proper responsive layout
  tableContainerStyle: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
  } as React.CSSProperties,

  // KPI Dashboard styles
  kpiGridStyle: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', // grid-cols-5
    gap: '1rem', // gap-4
    marginBottom: '1rem', // mb-4
  },
  kpiTotalStyle: {
    backgroundColor: '#3b82f6', // bg-blue-500
    color: 'white', // text-white
    padding: '1rem', // p-4
    borderRadius: '0.5rem', // rounded
    textAlign: 'center',
  },
  kpiNumberStyle: {
    fontSize: '2rem', // text-3xl
    fontWeight: 'bold', // font-bold
  },
  kpiStyle: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  kpiCountStyle: {
    fontSize: '3rem', // text-sm
    fontWeight: 'bold', // font-semibold
  },
  kpiHeaderStyle: {
    fontSize: '1rem', // text-sm
    fontWeight: 'bold', // font-semibold
  },
  kpiLabelStyle: {
    fontSize: '0.875rem', // text-sm
  },

  // Filter section styles
  filterSectionStyle: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '1rem',
  },
  buttonGroupStyle: {
    display: 'flex',
    gap: '0.5rem',
  },
  mapButtonStyle: {
    backgroundColor: '#2563eb',
    color: 'white',
    padding: '0.5rem 1rem',
    borderRadius: '0.25rem',
    border: 'none',
    cursor: 'pointer',
  },
  filterControlsStyle: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem', // space-x-4
  },
  searchInputStyle: {
    border: '1px solid #d1d5db', // border
    padding: '0.5rem', // p-2
    borderRadius: '0.25rem', // rounded
  },
  addButtonStyle: {
    backgroundColor: '#2563eb', // bg-blue-600
    color: 'white', // text-white
    padding: '0.5rem 1rem', // px-4 py-2
    borderRadius: '0.25rem', // rounded
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },

  // Table styles
  tableStyle: {
    width: '100%', // w-full
    borderCollapse: 'collapse',
    backgroundColor: 'white', // bg-white
    borderRadius: '0.5rem', // rounded
    overflow: 'hidden',
    boxShadow:
      '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', // shadow
  },
  tableHeaderStyle: {
    backgroundColor: '#f3f4f6', // bg-gray-100
  },
  tableHeaderCellStyle: {
    padding: '0.75rem', // p-3
    textAlign: 'left',
    fontSize: '0.875rem', // text-sm
    fontWeight: '600', // font-semibold
    color: '#374151', // text-gray-700
    borderBottom: '1px solid #e5e7eb', // border-b
  },
  tableCellStyle: {
    padding: '0.75rem', // p-3
    borderBottom: '1px solid #f3f4f6', // border-b border-gray-100
    fontSize: '0.875rem', // text-sm
  },
  tableRowHoverStyle: {
    backgroundColor: '#f9fafb', // hover:bg-gray-50
  },
  actionButtonStyle: {
    padding: '0.25rem 0.5rem', // px-2 py-1
    margin: '0 0.125rem', // mx-0.5
    fontSize: '0.75rem', // text-xs
    borderRadius: '0.25rem', // rounded
    border: 'none',
    cursor: 'pointer',
  },
  viewButtonStyle: {
    backgroundColor: '#3b82f6', // bg-blue-500
    color: 'white', // text-white
  },
  editButtonStyle: {
    backgroundColor: '#f59e0b', // bg-yellow-500
    color: 'white', // text-white
  },

  // Modal styles
  modalOverlayStyle: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)', // bg-black bg-opacity-50
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000, // Increased to ensure it appears above Leaflet map
  },
  modalContentStyle: {
    backgroundColor: 'white', // bg-white
    padding: '1.5rem', // p-6
    borderRadius: '0.5rem', // rounded
    boxShadow:
      '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', // shadow-xl
    maxWidth: '500px',
    width: '90%',
    maxHeight: '80vh',
    overflowY: 'auto',
  },
  modalTitleStyle: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    marginBottom: '1rem',
    color: '#1f2937',
  },
  mapModalContentStyle: {
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
    width: '90vw',
    height: '85vh',
    display: 'flex',
    flexDirection: 'column',
  },
  mapModalHeaderStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 1.5rem',
    borderBottom: '1px solid #e5e7eb',
  },
  modalCloseButtonStyle: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '1.5rem',
    cursor: 'pointer',
    color: '#6b7280',
    padding: '0.25rem',
    lineHeight: 1,
  },
  mapModalBodyStyle: {
    flex: 1,
    padding: '1rem',
    overflow: 'hidden',
  },

  // Form styles
  formInputStyle: {
    border: '1px solid #d1d5db', // border
    padding: '0.5rem', // p-2
    marginBottom: '0.5rem', // mb-2
    width: '100%', // w-full
    borderRadius: '0.25rem', // rounded
  },
  formTextareaStyle: {
    border: '1px solid #d1d5db', // border
    padding: '0.5rem', // p-2
    marginBottom: '0.5rem', // mb-2
    width: '100%', // w-full
    borderRadius: '0.25rem', // rounded
    minHeight: '80px',
    resize: 'vertical',
  },

  // Button styles
  buttonContainerStyle: {
    display: 'flex',
    justifyContent: 'flex-end', // justify-end
    gap: '0.5rem', // space-x-2
    marginTop: '1rem', // mt-4
  },
  saveButtonStyle: {
    backgroundColor: '#2563eb', // bg-blue-600
    color: 'white', // text-white
    padding: '0.5rem 1rem', // px-4 py-2
    borderRadius: '0.25rem', // rounded
    border: 'none',
    cursor: 'pointer',
  },
  cancelButtonStyle: {
    backgroundColor: '#d1d5db', // bg-gray-300
    color: '#374151', // text-gray-700
    padding: '0.5rem 1rem', // px-4 py-2
    borderRadius: '0.25rem', // rounded
    border: 'none',
    cursor: 'pointer',
  },

  // Map container styles
  mapContainerWrapperStyle: {
    width: '100%', // w-full
    height: '500px', // h-[500px]
  },
  mapContainer: {
    height: '100%',
    width: '100%',
  },

  // Additional table styles
  tableRowStyle: {
    backgroundColor: 'white',
  },
  tableRowExpiringStyle: {
    backgroundColor: '#fef3c7', // bg-yellow-100 for expiring items
  },
  tableCellActionStyle: {
    padding: '0.5rem', // p-2
    borderBottom: '1px solid #f3f4f6', // border-b border-gray-100
    fontSize: '0.875rem', // text-sm
    display: 'flex',
    gap: '0.5rem', // space-x-2
  },
  deleteButtonStyle: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#dc2626', // text-red-600
    cursor: 'pointer',
    padding: '0.25rem',
  },

  // Missing styles referenced in component
  kpiPendingStyle: {
    backgroundColor: '#f3f4f6', // bg-gray-100
    color: '#000000', // text-black
    padding: '0.75rem', // p-3
    borderRadius: '0.5rem', // rounded-lg
    textAlign: 'center',
  },
  filterSelectStyle: {
    border: '1px solid #d1d5db', // border
    padding: '0.5rem', // p-2
  },
  facilityCountStyle: {
    fontSize: '0.875rem', // text-sm
    color: '#4b5563', // text-gray-600
  },
  addButtonIconStyle: {
    marginRight: '0.5rem', // mr-2
  },
};

export default ManufacturerMap;
